///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Branded banner for recipients of a share link (?s=<slug>): names the
/// shared content, offers "Add to My Places" for placemark shares (local
/// placemarks work signed out, so recipients keep the pin with zero
/// friction), and carries the "Make your own" growth CTA — sign-up for
/// visitors, plain dismiss for existing users. Dismissing hides the
/// banner but keeps the shared content on screen.
///

import { Button } from '@/components/ui/button'
import { setActiveShare, setAuthOpen } from '@/redux/dialogSlice'
import { upsertPlacemark } from '@/redux/placemarksSlice'
import { upsertStory } from '@/redux/storiesSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { track } from '@/utils/analytics'
import { cStoryAuthoringEnabled } from '@/utils/featureGates'
import { BookOpen, Globe2, MapPin, XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { slotActionClass, slotDismissClass, slotPillClass, slotPriority, useNotificationSlot } from './NotificationSlot'

export function SharedContentBanner() {
	const dispatch = useAppDispatch()
	const activeShare = useAppSelector(state => state.dialog.activeShare)
	const preview = useAppSelector(state => state.dialog.sharedPlacemarkPreview)
	const storyPreview = useAppSelector(state => state.dialog.sharedStoryPreview)
	const signedIn = useAppSelector(state => !!state.user.userProfile)
	const alreadySaved = useAppSelector(state =>
		preview !== null && state.placemarks.placemarks[preview.id] !== undefined)
	const storyAlreadySaved = useAppSelector(state =>
		storyPreview !== null && state.stories.stories[storyPreview.id] !== undefined)
	const [placeAdded, setPlaceAdded] = useState(false)
	const [storyAdded, setStoryAdded] = useState(false)

	const visible = useNotificationSlot('shared-content', activeShare !== null, slotPriority.sharedContent)

	// Funnel event: once per appearance
	const capturedShown = useRef(false)
	useEffect(() => {
		if (!activeShare || capturedShown.current) return
		capturedShown.current = true
		track('share_link_opened', { share_type: activeShare.shareType })
	}, [activeShare])

	if (!activeShare || !visible) return null

	const handleAddPlace = () => {
		if (!preview || alreadySaved) return
		const now = new Date().toISOString()
		dispatch(upsertPlacemark({ ...preview, createdAt: now, updatedAt: now }))
		setPlaceAdded(true)
	}

	const handleSaveStory = () => {
		if (!storyPreview || storyAlreadySaved) return
		const now = new Date().toISOString()
		dispatch(upsertStory({ ...storyPreview, createdAt: now, updatedAt: now }))
		setStoryAdded(true)
	}

	const handleMakeYourOwn = () => {
		track('share_make_your_own_clicked', { signed_in: signedIn })
		if (!signedIn) {
			dispatch(setAuthOpen(true))
		}
		dispatch(setActiveShare(null))
	}

	const handleDismiss = () => {
		dispatch(setActiveShare(null))
	}

	const showAddPlace = activeShare.shareType === "placemark" && preview !== null
	// Saving a story only makes sense while the stories list is reachable
	const showSaveStory = cStoryAuthoringEnabled && activeShare.shareType === "story" && storyPreview !== null

	return (
		<div className={slotPillClass}>
			<Globe2 className="h-3.5 w-3.5 shrink-0 text-cyan-400" />
			<span className="text-xs text-white/90 truncate">
				<span className="font-medium text-white">{activeShare.title}</span>
				{" — shared with EarthBrowser"}
			</span>
			{showAddPlace && (
				<Button
					size="sm"
					onClick={handleAddPlace}
					disabled={placeAdded || alreadySaved}
					className={`${slotActionClass} bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-all duration-300 disabled:opacity-80`}
				>
					<MapPin className="h-3 w-3 mr-1" />
					{placeAdded || alreadySaved ? "Saved to My Places" : "Add to My Places"}
				</Button>
			)}
			{showSaveStory && (
				<Button
					size="sm"
					onClick={handleSaveStory}
					disabled={storyAdded || storyAlreadySaved}
					className={`${slotActionClass} bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-all duration-300 disabled:opacity-80`}
				>
					<BookOpen className="h-3 w-3 mr-1" />
					{storyAdded || storyAlreadySaved ? "Saved to My Stories" : "Save story"}
				</Button>
			)}
			<Button
				size="sm"
				onClick={handleMakeYourOwn}
				className={`${slotActionClass} bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-all duration-300 hover:scale-105`}
			>
				Make your own
			</Button>
			<button
				onClick={handleDismiss}
				aria-label="Dismiss shared content banner"
				className={slotDismissClass}
			>
				<XIcon className="h-3.5 w-3.5" />
			</button>
		</div>
	)
}
